import 'rc-tree/assets/index.css'

import { Icon } from '@iconify/react'
import { Computed, useObservable } from '@legendapp/state/react'
import { useMutation } from '@tanstack/react-query'
import {
  createFileRoute,
  useLoaderData,
} from '@tanstack/react-router'
import Tree from 'rc-tree'
import React, { Key, useState } from 'react'
import { toast } from 'react-toastify'
import { ulid } from 'ulidx'

import { fetchDivisionList, saveAddUpdateDivision } from '~features/divisions/api'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  IconButton,
  Input,
} from '~in-app/components'
import { auth$ } from '~in-app/legend-state'
import { Division } from '~in-app/shareable/division/types'

export const Route = createFileRoute('/console/divisions/list')({
  component: DivisionPage,
  loader: async ({ abortController }) => {
    let divisionIds = [] as any[]
    const privilegeValue = auth$.privileges.get()
    const userDivisionId = auth$.division.get()?.id
    if (privilegeValue.has('0.4.0.0')) {
      const divisionResponse = await fetchDivisionList({
        abortCtrl: abortController,
        query: {
          userDivisionID: userDivisionId ?? '',
        },
      })
      const allDivisions = divisionResponse.userDivisions // Store all divisions for reference

      // Function to recursively find all children of a given parent ID
      const findAllChildren = (items: Division[], parentId: string): any[] => {
        return items
          .filter(division => division.parentId === parentId)
          .map(division => ({
            key: division.id,
            title: division.name,
            parentId: division.parentId,
            children: findAllChildren(items, division.id), // Find children of this division
            version: division.version,
            updatedBy: division.updatedBy,
            updatedOn: division.updatedOn,
            createdBy: division.createdBy,
            createdOn: division.createdOn,
          }))
      }

      // Transform `divisionIds` with child information (if needed)
      divisionIds = allDivisions
        .filter((division: { parentId?: string }) => !division.parentId)
        .map((division: Division) => ({
          key: division.id,
          title: division.name,
          parentId: division.parentId,
          children: findAllChildren(division?.mychilds ?? [], division.id), // Find and attach children
          version: division.version,
          updatedBy: division.updatedBy,
          updatedOn: division.updatedOn,
          createdBy: division.createdBy,
          createdOn: division.createdOn,
        }))
    }
    // Ensure that the final return contains the fetched values.
    return { divisionIds, privilegeValue }
  },
  staticData: {
    privileges: ['0.3.0.0'],
  },
})

function DivisionPage() {
  const onDrop = (info: { node: { key: any, pos: string }, dragNode: { key: any }, dropPosition: number }) => {
    const { key: dropKey, pos } = info.node
    const { key: dragKey } = info.dragNode
    const dropPosition = info.dropPosition - Number(pos.split('-').pop())

    const loop = (data: any[], key: any, callback: (item: any, index: number, arr: any[]) => void) => {
      for (let i = 0; i < data.length; i++) {
        const item = data[i]
        if (item.key === key) {
          callback(item, i, data)
          return
        }
        if (item.children) {
          loop(item.children, key, callback)
        }
      }
    }

    const data = [...gData]

    // Find and remove dragObj from its original position
    let dragObj: any
    loop(data, dragKey, (item, index, arr) => {
      dragObj = { ...item } // Clone the item to avoid direct reference issues
      arr.splice(index, 1) // Remove the dragged item
    })

    if (dropPosition === 0) {
      // Drop on content
      loop(data, dropKey, (item) => {
        item.children = item.children || []
        dragObj.parentId = dropKey // Set the parentId before adding
        item.children.unshift(dragObj) // Add dragObj to the beginning
      })
    }
    else {
      // Drop in the gap (before or after)
      let targetArr: any[] = [] // Initialize to avoid "used before assigned" error
      let targetIndex = -1 // Initialize to avoid "used before assigned" error
      loop(data, dropKey, (_, index, arr) => {
        targetArr = arr
        targetIndex = index
      })

      dragObj.parentId = targetArr[targetIndex].key // Set the parentId before insertion

      if (dropPosition === -1) {
        targetArr.splice(targetIndex, 0, dragObj) // Insert before
      }
      else {
        targetArr.splice(targetIndex + 1, 0, dragObj) // Insert after
      }
    }

    setGData(data)
  }

  const onExpand = (expandedKeys: any) => {
    state$.expandedKeys.set(expandedKeys)
    state$.autoExpandParent.set(false)
  }
  const { divisionIds, privilegeValue } = useLoaderData({
    from: '/console/divisions/list',
  })
  const [gData, setGData] = useState(divisionIds)
  const state$ = useObservable({
    expandedKeys: divisionIds.flatMap((val: { key: string, children: any[] }) => {
      const keys = [val.key]
      const stack = [...val.children]
      while (stack.length > 0) {
        const item = stack.pop()
        keys.push(item.key)
        if (item.children) {
          stack.push(...item.children)
        }
      }
      return keys
    }) ?? [] as Key[],
    pagination: 0,
    pageSize: 20,
    queries: '',
    divisionIds: undefined,
    distributorIds: undefined,
    isChange: false,
    autoExpandParent: true,
  })
  const transformItem = (item: { [x: string]: any, children: any }) => {
    // Prepare the new structure for the item
    const { children, ...rest } = item // Separate children from the rest of the item
    const newItem: any = {
      ...rest,
      id: rest.isNew ? 'id' : rest.key,
      name: rest.title,
      parentId: rest.isNew ? undefined : rest.parentId,
      elements: children ? children.map(transformItem) : [], // Recursively transform children
    }
    delete newItem.isNew
    delete newItem.title
    delete newItem.key

    return newItem
  }
  const saveDivisionFunc = () => {
    const mutation = useMutation({
      mutationFn: saveAddUpdateDivision,
      onSuccess: (response) => {
        const setChildren = (children: any[]): any[] => {
          return children.map(item => ({
            key: item.id,
            title: item.name,
            parentId: item.parentId,
            children: item.elements ? setChildren(item.elements) : [],
            version: item.version,
            updatedBy: item.updatedBy,
            updatedOn: item.updatedOn,
            createdBy: item.createdBy,
            createdOn: item.createdOn,
          }))
        }
        const newResponse = setChildren(response.divisionPL)
        setGData(newResponse)
        toast.success(`Division Saved Successfully`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 5000,
        })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const saveDivisionMutation = saveDivisionFunc()

  const handleSaveDivision = () => {
    const submitValue = gData.map(transformItem) // Call the helper function

    saveDivisionMutation.mutate({ value: submitValue })
  }
  const [editableItems, setEditableItems] = useState<any>({}) // To track which items are editable
  const [editingTitle, setEditingTitle] = useState<{ [key: string]: string }>({})

  // Toggle edit state
  const handleEditToggle = (id: string) => {
    setEditableItems((prev: { [x: string]: any }) => ({
      ...prev,
      [id]: !prev[id],
    }))

    // Clear title when editing is stopped
    if (editableItems[id]) {
      setEditingTitle(prev => ({ ...prev, [id]: '' }))
    }
  }
  const updateTitleById = (data: any[], id: string, newTitle: string): any[] => {
    return data.map((item) => {
      if (item.key === id) {
        return { ...item, title: newTitle } // Update the title if the key matches
      }

      // If there are children, recursively update them
      if (item.children && item.children.length > 0) {
        return { ...item, children: updateTitleById(item.children, id, newTitle) }
      }

      return item // Return item as is if no updates are needed
    })
  }

  // Update title and gData
  const handleInputChange = (id: string, value: string) => {
    // Update editingTitle for live typing effect
    setEditingTitle(prev => ({ ...prev, [id]: value }))

    // Update gData with the new title
    setGData((prevGData: any[]) => updateTitleById(prevGData, id, value))
  }

  const handleInputBlur = (id: string) => {
    // Save the title to state$.gData when input loses focus
    const currentIndex = gData.findIndex((item: { key: string }) => item.key === id)
    if (currentIndex !== -1) {
      const newState = [...gData]
      newState[currentIndex].title = editingTitle[id] // Update the title in the state
      setGData(newState) // Update the global state
    }
    // Toggle edit mode off after saving
    handleEditToggle(id)
  }

  type TreeNode = {
    key: string
    title: string
    isNew: boolean
    children: TreeNode[]
  }
  const removeNode = (data: any[], keyToRemove: string): any[] => {
    return data.reduce((accumulator, current) => {
      // If the current item's key matches the key to remove
      if (current.key === keyToRemove) {
        // If the node has children, move them up to the parent level
        if (current.children && current.children.length > 0) {
          const movedChildren = current.children.map((child: any) => ({
            ...child,
            parentId: current.parentId || '', // Set the parentId to the deleted node's parentId or an empty string
          }))
          accumulator.push(...movedChildren) // Add children to the parent level
        }
        // Skip adding the current node to the accumulator to effectively delete it
        return accumulator
      }

      // If the current item has children, call removeNode recursively
      if (current.children) {
        current.children = removeNode(current.children, keyToRemove)
      }

      // Add the current item to the accumulator
      accumulator.push(current)
      return accumulator
    }, [])
  }

  // Example of handleDelete using the updated removeNode
  const handleDelete = React.useCallback((key: string) => {
    const updatedData = removeNode(gData, key)
    setGData(updatedData)
  }, [gData])

  const renderTitle = React.useCallback((node: TreeNode): React.ReactNode => {
    const { key, title, isNew } = node
    return (
      <div className="flex flex-col">
        <div className="flex items-center">
          {
            editableItems[key]
              ? (
                  <Input
                    className="h-8 flex-1"
                    type="text"
                    value={title}
                    onBlur={() => handleInputBlur(key)}
                    onChange={e => handleInputChange(key, e.target.value)}
                  />
                )
              : (
                  <span className="flex-1">{title}</span>
                )
          }

          <IconButton
            className="ml-3 size-6 bg-transparent"
            color="muted"
            disabled={!privilegeValue?.has('0.4.0.2')}
            onClick={() => handleEditToggle(key)}
          >
            <Icon
              className="size-4"
              icon={editableItems[key] ? 'lucide:check' : 'lucide:edit-2'}
            />
          </IconButton>

          {
            isNew && (
              <IconButton
                className="size-8"
                color="error"
                variant="text"
                onClick={() => handleDelete(key)} // Call handleDelete with the key of the node to delete
              >
                <Icon icon="lucide:trash-2" />
              </IconButton>
            )
          }
        </div>
      </div>
    )
  }, [editableItems, editingTitle, handleDelete, handleEditToggle, handleInputChange, handleInputBlur])

  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> Division Page</span>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!privilegeValue?.has('0.4.0.1')}
          onClick={
            () => {
              const id = ulid()
              const newItem = {
                key: id,
                title: 'Untitled', // Initialize with empty title
                parentId: '',
                children: [],
                isNew: true,
              }
              setGData((prevData: any) => [...prevData, newItem])
            }
          }
        >
          Add
        </Button>

      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div className="p-2">
              <Computed>
                {
                  () => (
                    <Tree
                      autoExpandParent={state$.autoExpandParent.get()}
                      draggable={{ icon: '↕️' }}
                      expandedKeys={state$.expandedKeys.get()}
                      itemHeight={500}
                      showIcon={false}
                      titleRender={renderTitle}
                      treeData={gData}
                      virtual={false}
                      // onDragEnter={onDragEnter}
                      // onDragStart={onDragStart}
                      onDrop={onDrop}
                      onExpand={onExpand}
                    />
                  )
                }
              </Computed>

            </div>
          </CardContent>

          <CardFooter>

            <Button
              fullWidth
              onClick={
                () => {
                  handleSaveDivision()
                }
              }
            >
              Save
            </Button>

          </CardFooter>
        </Card>
      </div>
    </div>
  )
}
